import React, { useEffect, useMemo, useState } from 'react';
import JsonQuery from 'json-query';
import { isValid } from 'date-fns';
import { customDatePicker } from '../';
import { useLocalization } from '../../hooks';
import { DateFilterOptions } from './';

interface DateFilterProps {
    data: any;
    onChangeSingleDate: (Date: any) => void;
    staticDataTabIndex?: number;
    isInCustomizedFundList?: boolean;
}

const SingleDateFilter: React.FC<DateFilterOptions & DateFilterProps> = (props) => {
    const { data, datelabel, ...rest } = props;
    const [startDate, setStartDate] = useState<any>(null);
    const [minDate, setMinDate] = useState<any>(null);
    const [maxDate, setMaxDate] = useState<any>(null);
    const i18n = useLocalization();

    // Extract the dates from the provided data using json-query
    const dates = useMemo(
        () => JsonQuery(rest?.dataKey || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.dataKey]
    );

    // get default key value if it exists
    const defaultDate = useMemo(
        () => JsonQuery(rest?.dateDefaultKey || '', { data }).value || '',
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.dateDefaultKey]
    );

    const limitedDates = useMemo(
        () => JsonQuery(rest?.limitDatekeySelection || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.limitDatekeySelection]
    );

    // Update the start and end date when the dates data changes
    useEffect(() => {
        if (dates?.length > 0) {
            let minDate = new Date(dates?.[0]);
            let maxDate = new Date(dates?.[0]);

            dates?.forEach((date: string) => {
                const currentDate = new Date(date);
                if (isValid(currentDate)) {
                    if (currentDate < minDate) {
                        minDate = currentDate;
                    }
                    if (currentDate > maxDate) {
                        maxDate = currentDate;
                    }
                }
            });

            if (isValid(minDate) && isValid(maxDate) && !rest?.isInCustomizedFundList) {
                setMinDate(minDate);
                setStartDate(minDate);
                setMaxDate(maxDate);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates]);

    // Call the onChangeDateRange callback when the start or end date changes
    useEffect(() => {
        if (startDate && isValid(startDate)) {
            rest.onChangeSingleDate(startDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate]);

    useEffect(() => {
        if (rest?.clearFilters) {
            setMinDate('');
            setStartDate('');
            setMaxDate('');
            rest.onChangeSingleDate('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest?.clearFilters]);

    return (
        <div className="date-filter-container">
            <div className="start-date-container">
                <span tabIndex={rest.disableLabelTabIndex ? -1 : rest.staticDataTabIndex}>
                    {i18n.format(datelabel, data)}
                </span>
                <customDatePicker.CustomDatePicker
                    {...rest}
                    id="startDateInput"
                    minDate={rest.limitDateMinToMinDate ? minDate : null}
                    maxDate={
                        rest.limitStartDateMaxToCurrentDate
                            ? new Date()
                            : rest?.limitStartDateMaxToMaxDate
                              ? maxDate
                              : null
                    }
                    staticDataTabIndex={0} // Setting this to zero as we
                    // don't have any static content in datepicker
                    selected={startDate}
                    isStartDate={true}
                    onChange={(date: any) => setStartDate(date)}
                    closeOnScroll
                    limitedDates={limitedDates}
                    limitDateStartErrorMessage={i18n.format(rest?.limitDateErrorMessage, data)}
                    defaultDate={defaultDate || ''}
                />
            </div>
        </div>
    );
};

export default SingleDateFilter;
